import React, { memo, useCallback, useMemo } from 'react';

import { Box, Divider } from '@mui/material';
import { ArrowCheckIcon } from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';

import {
  expandButtonStyles,
  railCountBadgeMutedStyles,
  railCountBadgeStyles,
  railDividerStyles,
  railIconRowStyles,
  railIconWrapStyles,
  railLabelStyles,
  railLeftBorderStyles,
  railRightBorderStyles,
  railTabButtonStyles,
  railWrapperStyles,
} from './CollapsedPaneRail.styles';
import { CollapsedPaneRailTestIds } from './CollapsedPaneRail.testIds';

/**
 * A small count badge overlaid on a rail icon. Muted when the count is zero.
 */
const CountBadge = ({ count }) => {
  if (count == null) {
    return null;
  }

  const badgeSx =
    count > 0 ? railCountBadgeStyles : { ...railCountBadgeStyles, ...railCountBadgeMutedStyles };

  return <Box sx={badgeSx}>{count}</Box>;
};

CountBadge.propTypes = { count: PropTypes.number };

/**
 * A single tab icon button for the collapsed inspector rail.
 * Extracted as a component to avoid inline arrow functions in the map callback.
 */
const RailTabButton = memo(({ tabKey, icon, count, active, onExpand, onExpandToTab }) => {
  const title = count != null ? `${tabKey} (${count})` : tabKey;

  const onClick = useCallback(() => {
    if (onExpandToTab) {
      onExpandToTab(tabKey);

      return;
    }

    onExpand();
  }, [tabKey, onExpand, onExpandToTab]);

  return (
    <Box sx={railIconWrapStyles}>
      <ButtonWithTooltip
        data-testid={CollapsedPaneRailTestIds.getTabButtonId(tabKey)}
        icon={icon}
        iconSize={IconSize[20]}
        size="small"
        sx={railTabButtonStyles}
        title={title}
        variant={active ? 'outlined' : 'text'}
        onClick={onClick}
      />
      <CountBadge count={count} />
    </Box>
  );
});

RailTabButton.propTypes = {
  icon: PropTypes.elementType.isRequired,
  tabKey: PropTypes.string.isRequired,
  onExpand: PropTypes.func.isRequired,
  active: PropTypes.bool,
  count: PropTypes.number,
  onExpandToTab: PropTypes.func,
};

/**
 * A 48px collapsed-state rail for a side panel.
 *
 * @param {{ side: 'left'|'right', label: string, iconName?: React.ElementType, count?: number, tabs?: Array<{key: string, icon: React.ElementType, count?: number, active?: boolean}>, onExpand: function, onExpandToTab?: function }} props
 */
const CollapsedPaneRail = ({
  side = 'left',
  label,
  iconName,
  count,
  tabs,
  onExpand,
  onExpandToTab,
}) => {
  const expandIconDirection = side === 'left' ? IconDirection.RIGHT : IconDirection.LEFT;
  const hasTabs = !!tabs?.length;
  const hasPrimaryIcon = !!iconName;
  const countLabel = count != null ? String(count) : undefined;

  const expandTitle = `Expand ${label}`;
  const primaryIconTitle = countLabel ? `${label} (${countLabel})` : label;

  const borderStyles = side === 'left' ? railLeftBorderStyles : railRightBorderStyles;

  const wrapperSx = useMemo(
    () => ({ ...railWrapperStyles, ...borderStyles }),
    // borderStyles is stable (a module-level constant reference), but we memoize
    // per side so the identity is stable across re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [side],
  );

  return (
    <Box sx={wrapperSx}>
      <ButtonWithTooltip
        data-testid={CollapsedPaneRailTestIds.getExpandButtonId(side)}
        icon={ArrowCheckIcon}
        iconDirection={expandIconDirection}
        iconSize={IconSize[24]}
        size="small"
        sx={expandButtonStyles}
        title={expandTitle}
        variant="outlined"
        onClick={onExpand}
      />

      <Divider flexItem sx={railDividerStyles} />

      <Box sx={railIconRowStyles}>
        {hasPrimaryIcon && (
          <Box sx={railIconWrapStyles}>
            <ButtonWithTooltip
              data-testid={CollapsedPaneRailTestIds.getPrimaryIconButtonId(side)}
              icon={iconName}
              iconSize={IconSize[20]}
              size="small"
              sx={railTabButtonStyles}
              title={primaryIconTitle}
              variant="text"
              onClick={onExpand}
            />
            <CountBadge count={count} />
          </Box>
        )}

        {hasTabs &&
          tabs.map(tab => (
            <RailTabButton
              active={tab.active}
              count={tab.count}
              icon={tab.icon}
              key={tab.key}
              tabKey={tab.key}
              onExpand={onExpand}
              onExpandToTab={onExpandToTab}
            />
          ))}
      </Box>

      <Box sx={railLabelStyles}>{label}</Box>
    </Box>
  );
};

CollapsedPaneRail.propTypes = {
  label: PropTypes.string.isRequired,
  onExpand: PropTypes.func.isRequired,
  count: PropTypes.number,
  iconName: PropTypes.elementType,
  side: PropTypes.oneOf(['left', 'right']),
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
      active: PropTypes.bool,
      count: PropTypes.number,
    }),
  ),
  onExpandToTab: PropTypes.func,
};

export default memo(CollapsedPaneRail);
